import { Box, FormControl } from '@mui/material';
import { useCallback } from 'react';
import { isEqual } from 'lodash';
import FieldLabel from '../../common/FieldLabel';
import { useSelector } from 'react-redux';
import {
  selectSlackIntegrationStatus,
  SlackConnectionStatus,
} from '../../../reducers/integrationsReducer';
import { selectProductionCrmOrg } from '../../pages/environments/environmentsReducer';
import { colors } from '@sweep-io/sweep-design';
import { SlackRecipientsSelect } from './SlackRecipientsSelect';
import { useSlackRecipients } from './useSlackRecipients';
import { SlackAddActionPanel } from './SlackAddActionPanel';
import { SlackNewMessageEditor } from './SlackNewMessageEditor';
import { SlackActionType } from '../../../types/enums/SlackActionType';
import { useGetSlackRecipientsLabels } from './useGetSlackRecipientsLabels';
import { SlackConstant } from './utils';
import { SlackAdditionalFieldsTable } from './SlackAdditionalFieldsTable';
import { useRunOnce } from '../../common/useRunOnce';

interface SlackNotificationActionProps {
  readonly?: boolean;
  action: SlackAutomationAction;
  crmOrgId: string;
  onChange: (slackAutomationAction: SlackAutomationAction) => any;
  objectName: string;
  showAssignmentSelector?: boolean;
}

export const SlackNotificationAction = ({
  readonly,
  action,
  crmOrgId,
  objectName,
  onChange,
  showAssignmentSelector,
}: SlackNotificationActionProps) => {
  const slackStatus = useSelector(selectSlackIntegrationStatus);
  const productionOrg = useSelector(selectProductionCrmOrg);
  const { recipientWithLabelsArray } = useGetSlackRecipientsLabels({
    recipients: action.actionParams?.recipients ?? [],
  });

  const updateOnChange = useCallback(
    (updateProps: Partial<AutomationSlackActionParams>) => {
      onChange({
        ...action,
        actionParams: {
          ...action.actionParams,
          ...updateProps,
        },
      });
    },
    [action, onChange],
  );

  useRunOnce(async () => {
    if (!action.actionParams?.fields) {
      updateOnChange({ fields: [] });
    }
  });

  const { isLoading } = useSlackRecipients({
    objectName,
    crmOrgId,
    addSweepSlackChannel: true,
  });

  const handleDeleteChip = useCallback(
    (chipToRemove: SlackRecipientStruct) => {
      const newRecipients = action.actionParams.recipients?.filter((item) => {
        if (chipToRemove.type === SlackConstant.Slack && item.type === SlackConstant.Slack) {
          return item.value !== chipToRemove.value;
        }
        if (
          chipToRemove.type === SlackConstant.Sweep_Created_Slack_Channel &&
          item.type === SlackConstant.Sweep_Created_Slack_Channel
        ) {
          return item.type !== SlackConstant.Sweep_Created_Slack_Channel;
        }
        if (
          chipToRemove.type === SlackConstant.Salesforce_Created_Slack_Channel &&
          item.type === SlackConstant.Salesforce_Created_Slack_Channel
        ) {
          return item.type !== SlackConstant.Salesforce_Created_Slack_Channel;
        }

        if (
          chipToRemove.type === SlackConstant.Salesforce &&
          item.type === SlackConstant.Salesforce
        ) {
          return !isEqual(
            (item.value as SlackFieldsStruct).fieldIds,
            (chipToRemove.value as SlackFieldsStruct).fieldIds,
          );
        }
        if (
          chipToRemove.type === SlackConstant.Assignment &&
          item.type === SlackConstant.Assignment
        ) {
          return item.value !== chipToRemove.value;
        }
        return true;
      });
      updateOnChange({ recipients: newRecipients });
    },
    [action, updateOnChange],
  );

  const _onChange = useCallback(
    (event: any, newValues: any) => {
      const newItems = newValues.map((el: any) => {
        switch (el.type) {
          case SlackConstant.Salesforce:
            return {
              ...el,
              type: SlackConstant.Salesforce,
              value: {
                fieldIds: el.value.fieldIds,
              },
            };
          case SlackConstant.Slack:
            return {
              ...el,
              type: SlackConstant.Slack,
              value: el.value,
            };
          case SlackConstant.Sweep_Created_Slack_Channel:
            return {
              ...el,
              type: SlackConstant.Sweep_Created_Slack_Channel,
            };
          case SlackConstant.Salesforce_Created_Slack_Channel:
            return {
              ...el,
              type: SlackConstant.Salesforce_Created_Slack_Channel,
            };
          case SlackConstant.Assignment:
            return {
              ...el,
              type: SlackConstant.Assignment,
              value: el.value,
            };
        }
      });
      updateOnChange({ recipients: newItems as SlackRecipientStruct[] });
    },
    [updateOnChange],
  );

  return (
    <>
      <Box
        sx={{
          background: colors.grey[100],
          padding: '24px 24px 19px',
          borderRadius: '8px',
          margin: '16px 0',
        }}
      >
        <FormControl
          sx={{
            width: '100%',
            marginBottom: '20px',
            maxWidth: '1148px',
          }}
        >
          <Box
            sx={{
              marginBottom: '3px',
              '& div': {
                color: colors.grey[800],
                fontSize: '12px',
                fontWeight: '400',
              },
            }}
          >
            <FieldLabel label={'Recipients'} markLabelAsRequired={true} />
          </Box>

          <SlackRecipientsSelect
            isAddTextFieldFromSf={true}
            customButtonText={showAssignmentSelector ? 'From Salesforce' : 'Add Dynamic Recipient'}
            objectName={objectName}
            crmOrgId={crmOrgId}
            isLoading={isLoading}
            onChange={_onChange}
            readonly={!!readonly}
            recipients={recipientWithLabelsArray}
            handleDeleteChip={handleDeleteChip}
            notConnected={!productionOrg || slackStatus !== SlackConnectionStatus.Connected}
            showAssignmentSelector={showAssignmentSelector}
          />
        </FormControl>

        <Box sx={{ position: 'relative' }}>
          <SlackNewMessageEditor
            isRequired={true}
            label="Message"
            readonly={readonly}
            messageStruct={action.actionParams?.message}
            crmOrgId={crmOrgId}
            onChange={(newMsg) => {
              updateOnChange({ message: newMsg });
            }}
            objectName={objectName}
            showAssignmentSelector={showAssignmentSelector}
          />
        </Box>

        <SlackAdditionalFieldsTable
          readonly={readonly}
          fields={action.actionParams?.fields}
          updateOnChange={(fields) => {
            updateOnChange({ fields });
          }}
          crmOrgId={crmOrgId}
          objectName={objectName}
          label={'Attached Fields'}
        />

        <SlackAddActionPanel
          crmOrgId={crmOrgId}
          objectName={objectName}
          slackActions={action.actionParams?.actions}
          onAddAction={(type: SlackActionType) => {
            switch (type) {
              case SlackActionType.UPDATE_RECORD: {
                const currActions = action.actionParams?.actions || [];
                const newUpdateAction: UpdateRecordSlackAction = {
                  buttonText: '',
                  fields: [],
                  type: SlackActionType.UPDATE_RECORD,
                };
                const _currActions = [...currActions, newUpdateAction];
                updateOnChange({ actions: _currActions });
                break;
              }
              case SlackActionType.CREATE_RECORD: {
                const currActions = action.actionParams?.actions || [];
                const newCreateAction: CreateRecordSlackAction = {
                  buttonText: '',
                  fields: [],
                  type: SlackActionType.CREATE_RECORD,
                  objectTypeName: {
                    label: '',
                    objectType: '',
                  },
                };
                updateOnChange({ actions: [...currActions, newCreateAction] });
                break;
              }
              case SlackActionType.LINK_BUTTON: {
                const currActions = action.actionParams?.actions || [];
                const newCreateAction: LinkButtonSlackAction = {
                  buttonText: '',
                  type: SlackActionType.LINK_BUTTON,
                  _valueType: 'dynamic',
                  url: {
                    template: '',
                    variables: [],
                  },
                };
                updateOnChange({ actions: [...currActions, newCreateAction] });
                break;
              }
              default:
                break;
            }
          }}
          onUpdateAction={(_action: SlackAction, index: number) => {
            const currActions = [...(action.actionParams?.actions || [])];
            currActions[index] = _action;
            updateOnChange({ actions: currActions });
          }}
          onDeleteAction={(index: number) => {
            const currActions = [...(action.actionParams?.actions || [])];
            currActions.splice(index, 1);
            updateOnChange({ actions: currActions });
          }}
          onReorderAction={(_actions: SlackAction[]) => {
            const currActions = [...(_actions || [])];
            updateOnChange({ actions: currActions });
          }}
        />
      </Box>
    </>
  );
};
